import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ErrorHandler,
  EventEmitter,
  Injector,
  Input,
  NgModule,
  NgModuleRef,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChange,
  SimpleChanges,
  Type,
  ViewContainerRef,
  createNgModuleRef,
} from '@angular/core';

/**
 * Components that aren't needed for the first paint (the item options modal,
 * the header's cart drawer, the recommendations popup, the AI chat) are kept
 * out of main.js: their code is fetched with import() the first time they
 * are shown. The loaders are in app/lazy-components.ts.
 */

/** What a loader resolves to: the NgModule that declares the component, and the component. */
export interface LazyComponentType<T = unknown> {
  module: Type<unknown>;
  component: Type<T>;
}

export type LazyComponentLoader<T = unknown> = () => Promise<LazyComponentType<T>>;

/**
 * Renders a lazily loaded component where the directive sits:
 *
 *   <ng-container
 *     *ngIf="open"
 *     [appLazyComponent]="loadThing"
 *     [lazyInputs]="{ state: state }"
 *     (lazyCreated)="onThingCreated($event)"
 *   ></ng-container>
 *
 * - The component appears once its chunk has loaded (instantly when it was
 *   prefetched), and is destroyed with the directive (e.g. by the *ngIf).
 * - Inputs: Angular 13 has no ComponentRef.setInput, so `lazyInputs` are
 *   assigned to the instance, its ngOnChanges is called by hand and its view
 *   is marked for check, so OnPush components update as they would with
 *   template bindings. Keep the object literal in the template: Angular only
 *   rebuilds it when a value in it changes.
 * - Outputs: subscribe in the `lazyCreated` handler (ref.instance.x.subscribe).
 * - Parent styles don't reach the host element: it doesn't get the parent's
 *   _ngcontent attribute, so a parent rule on `app-thing` needs ::ng-deep.
 */
@Directive({ selector: '[appLazyComponent]' })
export class LazyComponentDirective implements OnChanges, OnDestroy {
  @Input('appLazyComponent') loader: LazyComponentLoader | null = null;
  @Input() lazyInputs: Record<string, unknown> | null = null;
  @Output() lazyCreated = new EventEmitter<ComponentRef<any>>();

  private moduleRef: NgModuleRef<unknown> | null = null;
  private componentRef: ComponentRef<any> | null = null;
  private applied: Record<string, unknown> = {};
  private loading = false;
  private destroyed = false;

  constructor(
    private viewContainer: ViewContainerRef,
    private injector: Injector,
    private errorHandler: ErrorHandler
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.componentRef) {
      if (changes['lazyInputs']) this.applyInputs(false);
    } else if (!this.loading && this.loader) {
      this.load(this.loader);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.componentRef?.destroy();
    this.moduleRef?.destroy();
  }

  private load(loader: LazyComponentLoader): void {
    this.loading = true;
    loader().then(
      ({ module, component }) => {
        this.loading = false;
        if (this.destroyed) return;
        this.moduleRef = createNgModuleRef(module, this.injector);
        this.componentRef = this.viewContainer.createComponent(component, {
          ngModuleRef: this.moduleRef,
        });
        this.applyInputs(true);
        this.lazyCreated.emit(this.componentRef);
      },
      (error) => {
        // chunk failed to load (offline, or a deploy replaced it): the next
        // time the host shows this component it tries again
        this.loading = false;
        this.errorHandler.handleError(error);
      }
    );
  }

  private applyInputs(firstChange: boolean): void {
    const ref = this.componentRef;
    const changes: SimpleChanges = {};
    Object.entries(this.lazyInputs || {}).forEach(([name, value]) => {
      if (!firstChange && Object.is(this.applied[name], value)) return;
      changes[name] = new SimpleChange(this.applied[name], value, firstChange);
      ref.instance[name] = value;
      this.applied[name] = value;
    });
    if (!Object.keys(changes).length) return;
    if (typeof ref.instance.ngOnChanges === 'function') {
      ref.instance.ngOnChanges(changes);
    }
    ref.injector.get(ChangeDetectorRef).markForCheck();
  }
}

@NgModule({
  declarations: [LazyComponentDirective],
  exports: [LazyComponentDirective],
})
export class LazyComponentModule {}

/**
 * Fetches a lazy component's chunk in the background, so showing it later
 * doesn't wait for the network. Errors are ignored: showing it retries.
 */
export function prefetchLazyComponent(loader: LazyComponentLoader): void {
  loader().catch(() => undefined);
}

/**
 * Runs `callback` once the browser is idle (at the latest after `timeout` ms).
 * Safari has no requestIdleCallback, so there it is a plain timer. Not
 * patched by zone.js: wrap Angular state changes in NgZone.run.
 */
export function whenIdle(callback: () => void, timeout = 3000): void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, Math.min(timeout, 1500));
  }
}
